import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { container, item } from "../lib/motion";
import { InlineOrb } from "../components/Loader";
import { BRAND } from "../lib/brand";
import AdminCommunity from "../components/AdminCommunity";
import { Film, Quote, Library, Users, BarChart3, Plus, Trash2, MessageSquare, Pin } from "lucide-react";

const TABS = [
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "videos", label: "Videos", icon: Film },
  { id: "community", label: "Community", icon: MessageSquare },
  { id: "quotes", label: "Quotes", icon: Quote },
  { id: "resources", label: "Resources", icon: Library },
  { id: "users", label: "Users", icon: Users },
];
const CATS = ["Discipline", "Identity", "Healing", "Purpose", "Faith", "Mindfulness"];

export default function Admin() {
  const [tab, setTab] = useState("analytics");
  return (
    <Layout glow="split">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <motion.div initial="hidden" animate="show" variants={container}>
          <motion.span variants={item} className="eyebrow">Control Room</motion.span>
          <motion.h1 variants={item} className="font-head text-4xl lg:text-5xl font-light mt-4 mb-3 tracking-tight">Admin Dashboard</motion.h1>
          <motion.p variants={item} className="text-zinc-500 mb-10 font-light text-lg">Manage your content, community and analytics.</motion.p>
        </motion.div>
        <div className="flex gap-2 mb-10 flex-wrap">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-2.5 rounded-full text-sm flex items-center gap-2 transition-all ${tab === t.id ? "bg-[#4da6ff] text-white glow-blue" : "btn-glass"}`}>
              <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>
        <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {tab === "analytics" && <Analytics />}
          {tab === "videos" && <Videos />}
          {tab === "community" && <AdminCommunity />}
          {tab === "quotes" && <Quotes />}
          {tab === "resources" && <Resources />}
          {tab === "users" && <UsersTab />}
        </motion.div>
      </div>
    </Layout>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="py-10"><InlineOrb /></div>;
  const cards = [
    { label: "Users", v: data.users }, { label: "Videos", v: data.videos }, { label: "Quotes", v: data.quotes },
    { label: "Resources", v: data.resources }, { label: "Journal Entries", v: data.journal_entries },
    { label: "Community Posts", v: data.community_messages }, { label: "Check-ins", v: data.checkins },
    { label: "Subscribers", v: data.newsletter ?? 0 },
  ];
  return (
    <motion.div initial="hidden" animate="show" variants={container} className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="admin-analytics">
      {cards.map((c) => (
        <motion.div key={c.label} variants={item} className="glass-card p-7">
          <div className="font-head text-5xl font-extralight text-[#4da6ff]">{c.v}</div>
          <div className="text-zinc-500 text-sm mt-3 font-light">{c.label}</div>
        </motion.div>
      ))}
    </motion.div>
  );
}

function Videos() {
  const blank = { platform: "facebook", url: "", title: "", category: "", thumbnail: "" };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/watch").then((r) => setItems(r.data.filter((v) => v.platform !== "youtube")));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) { toast.error("Title and video link are required"); return; }
    setSaving(true);
    try { await api.post("/watch", { ...form, url: form.url.trim() }); toast.success("Video added"); setForm(blank); load(); }
    catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Check the link and try again"); }
    setSaving(false);
  };
  const del = async (id) => { await api.delete(`/watch/${id}`); load(); };
  const pin = async (v) => { await api.post(`/watch/${v.id}/pin`); toast.success(v.pinned ? "Unpinned" : "Pinned to the homepage"); load(); };
  return (
    <div data-testid="admin-videos">
      <p className="text-zinc-500 text-sm font-light mb-4">Paste a Facebook or TikTok video link. Visitors open it on the original platform. TikTok thumbnails are fetched automatically; for Facebook, optionally paste an image link.</p>
      <form onSubmit={add} className="glass-card p-6 mb-8 grid md:grid-cols-3 gap-3">
        <select value={form.platform} onChange={set("platform")} data-testid="admin-video-platform" className="field px-4 py-2.5 text-sm">
          <option value="facebook">Facebook</option>
          <option value="tiktok">TikTok</option>
        </select>
        <input value={form.url} onChange={set("url")} placeholder="Video link (https://...)" data-testid="admin-video-url" className="field px-4 py-2.5 text-sm md:col-span-2" />
        <input value={form.title} onChange={set("title")} placeholder="Title" data-testid="admin-video-title" className="field px-4 py-2.5 text-sm" />
        <input value={form.category} onChange={set("category")} placeholder="Category (optional)" data-testid="admin-video-category" className="field px-4 py-2.5 text-sm" />
        <input value={form.thumbnail} onChange={set("thumbnail")} placeholder="Thumbnail image link (optional)" data-testid="admin-video-thumbnail" className="field px-4 py-2.5 text-sm" />
        <button type="submit" disabled={saving} data-testid="admin-video-add" className="btn-primary py-2.5 text-sm font-medium flex items-center justify-center gap-2 md:col-span-3 disabled:opacity-60"><Plus className="w-4 h-4" /> {saving ? "Adding..." : "Add video"}</button>
      </form>
      {items.length === 0 && <p className="text-zinc-600 text-sm" data-testid="admin-videos-empty">No videos yet.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((v) => (
          <div key={v.id} className="glass-card overflow-hidden" data-testid={`admin-video-${v.id}`}>
            <img src={v.thumbnail || BRAND.vortex} alt="" className={`w-full aspect-video object-cover ${v.thumbnail ? "" : "opacity-60"}`} />
            <div className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0"><div className="text-sm font-medium line-clamp-1">{v.title}</div><div className="text-xs text-zinc-600">{v.platform === "tiktok" ? "TikTok" : "Facebook"}{v.category ? ` · ${v.category}` : ""}</div></div>
              <div className="flex gap-3 shrink-0">
                <button onClick={() => pin(v)} title={v.pinned ? "Unpin" : "Pin to homepage"} data-testid={`admin-video-pin-${v.id}`} className={`transition-colors ${v.pinned ? "text-[#4da6ff]" : "text-zinc-600 hover:text-white"}`}><Pin className="w-4 h-4" fill={v.pinned ? "currentColor" : "none"} /></button>
                <button onClick={() => del(v.id)} data-testid={`admin-video-delete-${v.id}`} className="text-zinc-600 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Quotes() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ text: "", author: "Spirit Admin", category: "Discipline" });
  const load = () => api.get("/quotes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); if (!form.text) return; await api.post("/quotes", form); toast.success("Quote added"); setForm({ ...form, text: "" }); load(); };
  const del = async (id) => { await api.delete(`/quotes/${id}`); load(); };
  return (
    <div data-testid="admin-quotes">
      <form onSubmit={add} className="glass-card p-6 mb-6 space-y-3">
        <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Quote text" rows={2} data-testid="admin-quote-text" className="w-full field px-4 py-2.5 text-sm resize-none" />
        <div className="grid md:grid-cols-3 gap-3">
          <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Author" className="field px-4 py-2.5 text-sm" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-quote-category" className="field px-4 py-2.5 text-sm">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          <button type="submit" data-testid="admin-quote-add" className="btn-primary py-2.5 text-sm font-medium flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add Quote</button>
        </div>
      </form>
      <div className="divide-y divide-white/5">
        {items.map((q) => (
          <div key={q.id} className="flex items-center justify-between gap-4 py-4 px-2" data-testid={`admin-quote-${q.id}`}>
            <div><p className="text-sm text-zinc-200 font-light">"{q.text}"</p><span className="text-xs text-[#4da6ff]">{q.category} · {q.author}</span></div>
            <button onClick={() => del(q.id)} className="text-zinc-600 hover:text-red-400 shrink-0 transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Resources() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: "Discipline", summary: "", content: "", read_minutes: 3, image: "", status: "published" });
  const load = () => api.get("/resources", { params: { all: true } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.summary) { toast.error("Title and summary required"); return; }
    await api.post("/resources", { ...form, read_minutes: Number(form.read_minutes) || 3 });
    toast.success("Article saved"); setForm({ ...form, title: "", summary: "", content: "", image: "" }); load();
  };
  const del = async (id) => { await api.delete(`/resources/${id}`); load(); };
  return (
    <div data-testid="admin-resources">
      <form onSubmit={add} className="glass-card p-6 mb-6 space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="admin-resource-title" className="field px-4 py-2.5 text-sm" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-resource-category" className="field px-4 py-2.5 text-sm">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
        <input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Short description / summary" data-testid="admin-resource-summary" className="w-full field px-4 py-2.5 text-sm" />
        <div className="grid md:grid-cols-2 gap-3">
          <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Image URL (optional — falls back to category art)" data-testid="admin-resource-image" className="field px-4 py-2.5 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" min="1" value={form.read_minutes} onChange={(e) => setForm({ ...form, read_minutes: e.target.value })} placeholder="Read minutes" data-testid="admin-resource-read-minutes" className="field px-4 py-2.5 text-sm" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="admin-resource-status" className="field px-4 py-2.5 text-sm">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
        <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Full article body (Markdown: use ## for headings, **bold** for emphasis)" rows={5} data-testid="admin-resource-content" className="w-full field px-4 py-2.5 text-sm resize-none font-mono" />
        <button type="submit" data-testid="admin-resource-add" className="btn-primary px-6 py-2.5 text-sm font-medium flex items-center gap-2"><Plus className="w-4 h-4" /> Save Article</button>
      </form>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((r) => (
          <div key={r.id} className="glass-card p-5 flex items-center justify-between gap-4" data-testid={`admin-resource-${r.id}`}>
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                {r.title}
                {r.status === "draft" && <span className="text-[10px] uppercase tracking-wide text-amber-400 border border-amber-400/30 rounded px-1.5 py-0.5">Draft</span>}
              </div>
              <span className="text-xs text-[#4da6ff]">{r.category} · {r.read_minutes || 3} min</span>
            </div>
            <button onClick={() => del(r.id)} className="text-zinc-600 hover:text-red-400 shrink-0 transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  return (
    <div className="glass-card overflow-hidden" data-testid="admin-users">
      <table className="w-full text-sm">
        <thead className="text-left text-zinc-500 border-b border-white/5">
          <tr><th className="p-4 font-medium">Name</th><th className="p-4 font-medium">Email</th><th className="p-4 font-medium">Role</th><th className="p-4 font-medium">Level</th><th className="p-4 font-medium">XP</th><th className="p-4 font-medium">Streak</th></tr>
        </thead>
        <tbody className="font-mono text-[13px]">
          {users.map((u) => (
            <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-user-${u.user_id}`}>
              <td className="p-4 font-sans">{u.name || "—"}</td>
              <td className="p-4 text-zinc-400">{u.email}</td>
              <td className="p-4"><span className="inline-flex items-center gap-1.5">{u.role === "admin" && <span className="w-1.5 h-1.5 rounded-full bg-[#4da6ff]" style={{ boxShadow: "0 0 8px #4da6ff" }} />}<span className={u.role === "admin" ? "text-[#4da6ff]" : "text-zinc-500"}>{u.role}</span></span></td>
              <td className="p-4">{u.level}</td><td className="p-4">{u.xp}</td><td className="p-4">{u.streak}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
